import { useDispatch } from "react-redux";
import { AnyAction, ThunkAction, ThunkDispatch, configureStore } from "@reduxjs/toolkit";
import type { combineReducers } from "@reduxjs/toolkit";

import { appUsersApiSlice } from "./modules/app-users/store/app-users.rtkq";
import { assetStocksApiSlice } from "./modules/asset-stocks/store";
import { chemicalsApiSlice } from "./modules/chemicals/store/chemicals.rtkq";
import { cropsApiSlice } from "./modules/crop/store/crops.rtkq";
import { cropTypesApi } from "./modules/crop-types/store/crop-types.rtkq";
import { diseasesApiSlice } from "./modules/diseases/store/diseases.rtkq";
import { materiallyAccountableEmployeesSlice } from "./modules/employees/store";
import { employeesApiSlice } from "./modules/employees/store/employees.rtkq";
import { currentFarmLandsApiSlice } from "./modules/farm-lands/current-farm-lands.rtkq";
import { farmsApi } from "./modules/farms/store/farms.rtkq";
import { fertilisersApiSlice } from "./modules/fertilisers/store/fertilisers.rtkq";
import { departmentsApi } from "./modules/info-data/store/departments.rtkq";
import { measuresApi } from "./modules/info-data/store/measures.rtkq";
import { positionsApi } from "./modules/info-data/store/positions.rtkq";
import { seasonsApi } from "./modules/info-data/store/seasons.rtkq";
import { techOperationGroupsApi } from "./modules/info-data/store/tech-operation-groups.rtkq";
import { techOperationScalarsApi } from "./modules/info-data/store/tech-operation-scalars.rtkq";
import { techOperationSubGroupsApi } from "./modules/info-data/store/tech-operation-subgroups.rtkq";
import { techOperationMeasuresSetsApi } from "./modules/info-data/store/tech-operations-measures-sets.rtkq";
import { techOperationParamsApi } from "./modules/info-data/store/tech-operations-params.rtkq";
import { workPlacesApi } from "./modules/info-data/store/workplaces.rtkq";
import { pestsApiSlice } from "./modules/pests/store/pests.rtkq";
import { phenophasesApi } from "./modules/phenophases/store/phenophases.rtkq";
import { seedsApiSlice } from "./modules/seeds/store/seeds.rtkq";
import { techAssetsApiSlice } from "./modules/tech-assets/store/techop-assets.rtkq";
import { weedsApiSlice } from "./modules/weeds/store/weeds.rtkq";
import { assetStockBalancesApiSlice } from "./pages/asset-stocks/store/asset-stocks.rtkq";
import { weatherApi } from "./pages/fields/store/farm-lands-weather.rtkq";
import { observationsApiSlice } from "./pages/observations/store/observations.rtkq";
import { techopsRelatedApiSlice } from "./pages/tech-operations/store/techop-related.rtkq";
import { techopsApiSlice } from "./pages/tech-operations/store/techop.rtkq";
import { techWorksApiSlice } from "./pages/tech-works/store/tech-works.rtkq";
import { dynamicReducers } from "./reducers";

// Функция, не объект: в ts-jest циклические импорты могут дойти сюда раньше reducers.ts, dynamicReducers будет undefined.
export const createRootReducer = () => ({
  ...dynamicReducers,
  [techopsApiSlice.reducerPath]: techopsApiSlice.reducer,
  [techopsRelatedApiSlice.reducerPath]: techopsRelatedApiSlice.reducer,
  [currentFarmLandsApiSlice.reducerPath]: currentFarmLandsApiSlice.reducer,
  [techAssetsApiSlice.reducerPath]: techAssetsApiSlice.reducer,
  [materiallyAccountableEmployeesSlice.reducerPath]:
    materiallyAccountableEmployeesSlice.reducer,
  [assetStocksApiSlice.reducerPath]: assetStocksApiSlice.reducer,
  [assetStockBalancesApiSlice.reducerPath]: assetStockBalancesApiSlice.reducer,
  [observationsApiSlice.reducerPath]: observationsApiSlice.reducer,
  [techWorksApiSlice.reducerPath]: techWorksApiSlice.reducer,
  [employeesApiSlice.reducerPath]: employeesApiSlice.reducer,
  [seedsApiSlice.reducerPath]: seedsApiSlice.reducer,
  [appUsersApiSlice.reducerPath]: appUsersApiSlice.reducer,
  [chemicalsApiSlice.reducerPath]: chemicalsApiSlice.reducer,
  [diseasesApiSlice.reducerPath]: diseasesApiSlice.reducer,
  [fertilisersApiSlice.reducerPath]: fertilisersApiSlice.reducer,
  [pestsApiSlice.reducerPath]: pestsApiSlice.reducer,
  [weedsApiSlice.reducerPath]: weedsApiSlice.reducer,
  [cropsApiSlice.reducerPath]: cropsApiSlice.reducer,
  [weatherApi.reducerPath]: weatherApi.reducer,
  [cropTypesApi.reducerPath]: cropTypesApi.reducer,
  [phenophasesApi.reducerPath]: phenophasesApi.reducer,
  [farmsApi.reducerPath]: farmsApi.reducer,
  [departmentsApi.reducerPath]: departmentsApi.reducer,
  [workPlacesApi.reducerPath]: workPlacesApi.reducer,
  [seasonsApi.reducerPath]: seasonsApi.reducer,
  [positionsApi.reducerPath]: positionsApi.reducer,
  [techOperationGroupsApi.reducerPath]: techOperationGroupsApi.reducer,
  [techOperationSubGroupsApi.reducerPath]: techOperationSubGroupsApi.reducer,
  [techOperationScalarsApi.reducerPath]: techOperationScalarsApi.reducer,
  [techOperationMeasuresSetsApi.reducerPath]: techOperationMeasuresSetsApi.reducer,
  [techOperationParamsApi.reducerPath]: techOperationParamsApi.reducer,
  [measuresApi.reducerPath]: measuresApi.reducer,
});

export type RootState = ReturnType<
  ReturnType<typeof combineReducers<ReturnType<typeof createRootReducer>>>
>;

export const createAppStore = (preloadedState?: RootState) =>
  configureStore({
    devTools: { trace: true, maxAge: 20 },
    reducer: createRootReducer(),
    preloadedState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: false,
        immutableCheck: false,
      }).concat(
        techopsApiSlice.middleware,
        techopsRelatedApiSlice.middleware,
        currentFarmLandsApiSlice.middleware,
        techAssetsApiSlice.middleware,
        materiallyAccountableEmployeesSlice.middleware,
        assetStocksApiSlice.middleware,
        assetStockBalancesApiSlice.middleware,
        observationsApiSlice.middleware,
        techWorksApiSlice.middleware,
        employeesApiSlice.middleware,
        seedsApiSlice.middleware,
        appUsersApiSlice.middleware,
        chemicalsApiSlice.middleware,
        diseasesApiSlice.middleware,
        fertilisersApiSlice.middleware,
        pestsApiSlice.middleware,
        weedsApiSlice.middleware,
        cropsApiSlice.middleware,
        phenophasesApi.middleware,
        weatherApi.middleware,
        cropTypesApi.middleware,
        farmsApi.middleware,
        positionsApi.middleware,
        seasonsApi.middleware,
        workPlacesApi.middleware,
        departmentsApi.middleware,
        techOperationGroupsApi.middleware,
        techOperationSubGroupsApi.middleware,
        techOperationScalarsApi.middleware,
        measuresApi.middleware,
        techOperationMeasuresSetsApi.middleware,
        techOperationParamsApi.middleware
      ),
  });

export const store = createAppStore();

farmsApi.endpoints.fetchFarms.initiate();

export type AppDispatch = ThunkDispatch<RootState, undefined, AnyAction>;

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  AnyAction
>;

// Export a hook that can be reused to resolve types
export const useAppDispatch = (): AppDispatch => useDispatch<AppDispatch>();
